import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Search,
  Globe,
  Bot,
  Link as LinkIcon,
  FileCheck,
  Settings,
  Home,
  TrendingUp,
  BookOpen,
  Power,
  AlertTriangle,
  Shield,
  Building,
  ClipboardCheck,
  FolderOpen,
  Timer
} from 'lucide-react';

const navigationItems = [
  { path: '/', label: 'Accueil', icon: Home },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/pre-audit', label: 'Pré-Audit', icon: ClipboardCheck },
  { path: '/audit', label: 'Audit SEO', icon: Search },
  { path: '/keywords', label: 'Mots-clés', icon: TrendingUp },
  { path: '/geo', label: 'GEO Local', icon: Globe },
  { path: '/hotels', label: 'Hôtels', icon: Building },
  { path: '/chatbot', label: 'Chatbot IA', icon: Bot },
  { path: '/backlinks', label: 'Backlinks', icon: LinkIcon },
  { path: '/reports', label: 'Rapports', icon: FolderOpen },
  { path: '/time-estimation', label: 'Temps Premium', icon: Timer },
  { path: '/proposal', label: 'Proposition', icon: FileCheck },
  { path: '/lexique', label: 'Lexique', icon: BookOpen },
  { path: '/administration', label: 'Administration', icon: Shield },
  { path: '/settings', label: 'Paramètres', icon: Settings },
];

export const Navigation: React.FC = () => {
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { signOut, profile } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
      // Supabase Auth va gérer la redirection automatiquement
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <nav className="bg-gradient-to-b from-gray-900 via-gray-900 to-black p-6 space-y-6 h-full flex flex-col">
      <div className="mb-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h1 
              className="text-3xl font-bold" 
              style={{
                background: 'linear-gradient(to right, #E9BC66, #F4D89E)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              STRATOS
            </h1>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800/50 transition-all duration-200"
              title="Déconnexion"
            >
              <Power className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-400">Audit SEO & GEO Premium</p>
          {profile && (
            <div className="mt-2 text-xs text-gray-500">
              {profile.full_name || 'Utilisateur'}
              {profile.role === 'admin' && (
                <span className="ml-2 px-2 py-0.5 bg-stratos-gold/20 text-stratos-gold rounded">
                  Admin
                </span>
              )}
            </div>
          )}
          <div 
            className="h-px mt-4" 
            style={{
              background: 'linear-gradient(to right, #E9BC66, #D4A84F, transparent)'
            }}
          />
        </motion.div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900">Confirmer la déconnexion</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Vous devrez saisir à nouveau votre code PIN pour accéder à la plateforme.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
              >
                Se déconnecter
              </button>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-medium transition-colors duration-200"
              >
                Annuler
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <div className="flex-1 space-y-1">
        {navigationItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
                  isActive
                    ? "font-semibold shadow-lg"
                    : "text-gray-300 hover:bg-gray-800/50"
                )}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = '#E9BC66';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = '';
                  }
                }}
                style={isActive ? {
                  backgroundColor: '#E9BC66',
                  color: '#1A1A1A',
                  boxShadow: '0 10px 15px -3px rgba(233, 188, 102, 0.2)'
                } : {}}
              >
                <Icon 
                  className="w-5 h-5" 
                  style={isActive ? { color: '#1A1A1A' } : {}}
                />
                <span>{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="ml-auto w-2 h-2 bg-gray-900 rounded-full"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Footer avec branding */}
      <div className="pt-6 border-t border-gray-800">
        <div className="text-center space-y-3">
          <p className="text-xs text-gray-500">Powered by</p>
          <img 
            src="/img/stratos.png" 
            alt="STRATOS Consulting" 
            className="w-24 h-24 mx-auto object-contain"
          />
          <p className="text-xs text-gray-500">© {new Date().getFullYear()} - Tous droits réservés</p>
        </div>
      </div>
    </nav>
  );
};